import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlType } from '../../enums/control-type.enum';
import { ControlSize } from '../../enums/control-size.enum';

const CheckboxComponentProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CheckboxComponent),
  multi: true,
};

/**
 * The checkbox component
 */
@Component({
  selector: 'ideom-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [CheckboxComponentProvider],
})
export class CheckboxComponent implements ControlValueAccessor {
  /**
   * The flag indicating that control is disabled
   */
  @Input() disabled: boolean;

  /**
   * The input name
   */
  @Input() name: string;

  /**
   * The input label
   */
  @Input() label: string;

  /**
   * The flag indicating that checkbox should have middle state
   */
  @Input() middle: boolean;

  /**
   * The size of control
   */
  @Input() size: ControlSize = ControlSize.Medium;

  /**
   * The type of control
   */
  @Input() type: ControlType = ControlType.Primary;

  /**
   * The checkbox value
   */
  value: boolean | undefined;

  /**
   * The checkbox marker
   */
  marker: string;

  /**
   * @ignore
   */
  onChange: (value: boolean | undefined) => void;

  /**
   * @ignore
   */
  onTouched: () => void;

  /**
   * @ignore
   */
  registerOnChange(fn: (value: boolean | undefined) => void): void {
    this.onChange = fn;
  }

  /**
   * @ignore
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * @ignore
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * @ignore
   */
  writeValue(value: boolean | undefined): void {
    this.value = value;
    this.checkMarker();
  }

  /**
   * This method is used for emitting updated value
   */
  onChangeValue(): void {
    if (!this.disabled && this.onChange) {
      switch (this.value) {
        case true:
          this.value = this.middle ? undefined : false;
          break;
        case false:
          this.value = true;
          break;
        case undefined:
          this.value = false;
          break;
      }
      this.onChange(this.value);
      this.checkMarker();
    }
  }

  /**
   * This method is used for updating marker value
   * @private
   */
  private checkMarker(): void {
    this.marker = this.value !== false ? (this.value === true ? '✓' : '-') : null;
  }

  /**
   * Gets a list of classes to be applied to the control
   */
  get classes(): string[] {
    const classes: string[] = [];
    classes.push('checkbox-' + this.size);
    if (this.value === true || (this.middle && this.value === undefined)) {
      classes.push('background-' + this.type);
      classes.push('border-' + this.type);
    } else {
      classes.push('background-white');
    }
    return classes;
  }
}
